import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Layers, Download, GitBranch, Loader2, Lock } from "lucide-react";
import { exportCurrentLayer, exportMasterTree } from "../lib/anki";
import { toast } from "sonner";

export default function AnkiExportDialog({ open, onOpenChange, currentGrid, allGrids, rootName, isPremium, onUpgrade }) {
  const [busy, setBusy] = useState(null);
  const treeCardCount = allGrids.reduce((n, g) => n + g.elements.length, 0);

  const doCurrent = async () => {
    setBusy("current");
    try {
      await exportCurrentLayer(currentGrid);
      toast.success("Current layer .apkg downloaded");
      onOpenChange(false);
    } catch (e) {
      toast.error("Export failed. Please retry.");
    } finally { setBusy(null); }
  };

  const doTree = async () => {
    if (!isPremium) { onOpenChange(false); onUpgrade("Master Tree export is a Premium feature."); return; }
    setBusy("tree");
    try {
      await exportMasterTree(allGrids, rootName);
      toast.success(`Master tree .apkg downloaded (${treeCardCount} cards)`);
      onOpenChange(false);
    } catch (e) {
      toast.error("Export failed. Please retry.");
    } finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="anki-export-dialog" className="max-w-md bg-[#0D0B18] border border-purple-500/25 text-white">
        <DialogTitle className="sr-only">Export to Anki</DialogTitle>
        <DialogDescription className="sr-only">Download your flashcards as an Anki package.</DialogDescription>
        <h3 className="font-display text-xl font-bold text-white">Export to Anki</h3>
        <p className="text-sm text-purple-200/70 -mt-1">
          Downloads a real Anki <span className="font-mono-fx text-purple-300">.apkg</span> with{" "}
          <span className="font-mono-fx text-purple-300">::</span> hierarchical sub-decks. Just double-click to import.
        </p>

        <button data-testid="anki-export-current-btn" onClick={doCurrent} disabled={busy}
          className="mt-2 w-full text-left rounded-2xl p-4 bg-purple-500/8 border border-purple-500/25 hover:bg-purple-500/18 hover:border-purple-400/50 transition-all group disabled:opacity-60">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-purple-500/15 border border-purple-500/25">
              <Layers className="w-5 h-5 text-purple-300" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-white">Export Current Layer</p>
              <p className="text-xs text-purple-200/60">{currentGrid.elements.length} cards from this grid</p>
            </div>
            {busy === "current" ? <Loader2 className="w-4 h-4 animate-spin text-purple-300" /> : <Download className="w-4 h-4 text-purple-400 group-hover:text-purple-200" />}
          </div>
        </button>

        <button data-testid="anki-export-master-deck-btn" onClick={doTree} disabled={busy}
          className="w-full text-left rounded-2xl p-4 bg-gradient-to-br from-purple-700/25 to-purple-950/40 border border-purple-400/40 hover:border-purple-300/60 transition-all group shadow-[0_0_20px_rgba(168,85,247,0.2)] disabled:opacity-60">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-purple-500/20 border border-purple-400/30">
              <GitBranch className="w-5 h-5 text-purple-200" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-white flex items-center gap-2">
                Export Master Tree Deck {!isPremium && <Lock className="w-3.5 h-3.5 text-amber-300" />}
              </p>
              <p className="text-xs text-purple-200/60">{treeCardCount} cards across {allGrids.length} explored branches</p>
            </div>
            {busy === "tree" ? <Loader2 className="w-4 h-4 animate-spin text-purple-300" /> : <Download className="w-4 h-4 text-purple-300 group-hover:text-white" />}
          </div>
        </button>
      </DialogContent>
    </Dialog>
  );
}
